import { useEffect, useRef, useState } from "react";

function isEditableElement(element) {
  if (!element || element === document.body) return false;
  const tag = element.tagName;
  if (tag === "TEXTAREA") return true;
  if (tag === "INPUT") {
    const type = String(element.type || "text").toLowerCase();
    return !["button", "checkbox", "file", "hidden", "radio", "submit", "reset", "image"].includes(
      type
    );
  }
  return Boolean(element.isContentEditable);
}

function readVisualViewportState(baselineHeight, keyboardThreshold = 0.82) {
  if (typeof window === "undefined") {
    return { keyboardOpen: false };
  }

  const viewport = window.visualViewport;
  const focusedEditable = isEditableElement(document.activeElement);

  if (!viewport || !baselineHeight) {
    return { keyboardOpen: focusedEditable };
  }

  const viewportKeyboard = viewport.height < baselineHeight * keyboardThreshold;

  return {
    keyboardOpen: focusedEditable || viewportKeyboard,
  };
}

/**
 * Detecta teclado virtual no mobile.
 * Com interactive-widget=resizes-content o innerHeight encolhe junto — usamos
 * altura inicial do viewport como baseline e foco em campos editáveis.
 */
export function useMobileVisualViewport(enabled = true, keyboardThreshold = 0.82) {
  const baselineRef = useRef(0);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    if (!baselineRef.current) {
      baselineRef.current = Math.max(
        viewport.height,
        window.innerHeight,
        window.screen?.availHeight ? window.screen.availHeight * 0.85 : 0
      );
    }

    const update = () => {
      setKeyboardOpen(readVisualViewportState(baselineRef.current, keyboardThreshold).keyboardOpen);
    };

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    document.addEventListener("focusin", update);
    const onFocusOut = () => window.setTimeout(update, 120);
    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("orientationchange", () => {
      baselineRef.current = Math.max(viewport.height, window.innerHeight);
      update();
    });

    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [enabled, keyboardThreshold]);

  return { keyboardOpen };
}
